import { useState, useEffect } from "react";
import api from "../lib/api";
import PodLayout from "../components/PodLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { FileText, Plus, DownloadSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  "School and Administrative Resources", "Teaching and Tutoring Resources",
  "Leadership and Career Resources", "Business and Financial Resources",
];

export default function PodResourcesPage() {
  const { user } = useAuth();
  const isStaff = ["super_admin", "admin", "instructor"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "", subcategory: "", description: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/pod/resources").then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = items.filter(r => (!cat || r.category === cat) && (!search || (r.title || "").toLowerCase().includes(search.toLowerCase())));

  const upload = async () => {
    if (!file || !form.title || !form.category) return toast.error("Title, category, file required");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("subcategory", form.subcategory);
    fd.append("description", form.description);
    setSaving(true);
    try {
      await api.post("/pod/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resource uploaded");
      setOpen(false); setForm({ title: "", category: "", subcategory: "", description: "" }); setFile(null);
      load();
    } catch (e) { toast.error("Upload failed"); }
    setSaving(false);
  };

  const download = (id) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pod/resources/${id}/download`, "_blank");
  };
  const del = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try { await api.delete(`/pod/resources/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  return (
    <PodLayout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="pod-resources-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">POD</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Resource Library</h1>
          </div>
          {isStaff && <Button onClick={() => setOpen(true)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="upload-resource-btn"><Plus size={14} weight="bold" /> Upload Resource</Button>}
        </div>

        <div className="flex gap-3 mb-6 flex-wrap items-center">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search resources…" className="rounded-none pl-9" data-testid="search-resources-input" />
          </div>
          <Select value={cat} onValueChange={v => setCat(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-none max-w-xs"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">{filtered.length} resource{filtered.length === 1 ? "" : "s"}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.length === 0 && <p className="text-sm text-slate-500 col-span-full">No resources here yet.</p>}
          {filtered.map(r => (
            <Card key={r.id} className="rounded-none border border-slate-200 hover:shadow-md transition-all">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-2">
                  <div className="bg-amber-100 text-amber-800 w-10 h-10 flex items-center justify-center"><FileText size={20} weight="fill" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-950 text-sm leading-snug truncate">{r.title}</p>
                    <p className="text-xs text-slate-500 truncate">{r.category}{r.subcategory ? ` · ${r.subcategory}` : ""}</p>
                  </div>
                </div>
                {r.description && <p className="text-xs text-slate-600 mb-3 line-clamp-3">{r.description}</p>}
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">{r.downloads || 0} download{r.downloads === 1 ? "" : "s"}</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => download(r.id)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-1 text-xs" data-testid={`download-resource-${r.id}`}><DownloadSimple size={12} /> Download</Button>
                  {isStaff && <Button size="sm" variant="outline" onClick={() => del(r.id)} className="rounded-none gap-1 text-xs text-red-700 border-red-200" data-testid={`delete-resource-${r.id}`}><Trash size={12} /></Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle>Upload Resource</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="resource-title-input" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Subcategory (optional)</Label><Input value={form.subcategory} onChange={e => setForm({ ...form, subcategory: e.target.value })} className="rounded-none" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none" /></div>
              <div><Label>File</Label><Input type="file" onChange={e => setFile(e.target.files?.[0])} className="rounded-none" data-testid="resource-file-input" /></div>
              <Button disabled={saving} onClick={upload} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none w-full" data-testid="save-resource-btn">{saving ? "Uploading…" : "Upload Resource"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PodLayout>
  );
}
